// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

import { closeButton, openOverlay } from './overlay';
import type { Meta } from './types';
import { num, shortDate } from './format';

/**
 * The About panel is where every caveat that would otherwise be buried lives.
 *
 * It leads with the limits rather than the features, because the single most
 * damaging thing this site could do is let someone conclude "not listed here,
 * therefore safe".
 */
export function openAbout(meta: Meta): void {
  openOverlay({
    panelClass: 'modal',
    label: 'About this site',
    lockScroll: true,
    build(panel, close) {
      const head = document.createElement('div');
      head.className = 'overlay-head';
      const h = document.createElement('h2');
      h.textContent = 'About Product Recalls';
      head.append(h, closeButton(close, 'Close about panel'));

      const body = document.createElement('div');
      body.className = 'stack small';
      body.innerHTML = `
        <p>Australia publishes its recalls in two separate places. This site reads both of them in
        full, makes each searchable, and then does the thing neither publisher does: analyses them —
        each on its own terms, and never added together.</p>

        <div class="note">
          <strong>If your product is not listed, that does not mean it is safe.</strong>
          It means no recall has been published for it. A product can be dangerous and
          never recalled, and a recall can describe a batch or date range that your item
          may or may not fall inside. Always check the recall notice itself, and when in
          doubt contact the supplier.
        </div>

        <h3>Where the data comes from</h3>
        <p><strong>Consumer products</strong> — the ACCC's Product Safety Australia recall
        register: <span class="mono">${num(meta.accc.total)}</span> recalls published between
        ${meta.accc.firstYear} and ${meta.accc.lastYear}. Each one carries the supplier, the
        retailers who sold it, what was wrong with it, what could happen to a person, and
        what you should do.</p>
        <p><strong>Vehicles</strong> — the Department of Infrastructure's vehicle recall
        register: <span class="mono">${num(meta.vehicle.total)}</span> campaigns. This register
        publishes something the consumer one does not: how many vehicles each campaign
        affects.</p>
        <p>Data as at <strong>${shortDate(meta.asAt)}</strong>. Refreshed monthly.</p>

        <h3>Not every entry in the ACCC's list is an ACCC record</h3>
        <p>This is not obvious from the register, and it matters for what you will find here.
        ${meta.accc.bySource ? `Of ${num(meta.accc.total)} entries, <strong>${num(meta.accc.bySource.accc ?? 0)}</strong>
        are ordinary consumer product recalls with the ACCC's own description of the fault and the hazard.
        <strong>${num(meta.accc.bySource.fsanz ?? 0)}</strong> are <em>food</em> recalls: the ACCC lists them, but
        its page for each one redirects to Food Standards Australia New Zealand, which is where the detail
        actually lives — so this site shows the entry and links onward rather than inventing a description.
        A further <strong>${num(meta.accc.bySource.vehicle ?? 0)}</strong> are <em>vehicle</em> recalls that also appear
        in the vehicle register, matched by their REC number.` : ''}</p>

        <h3>Things worth knowing before you read the charts</h3>
        <ul>
          <li><strong>The two registers are kept apart.</strong> They have different categories and
          different date meanings, and are never added together — a combined total would be a number about
          nothing. The one real link between them is the small set of vehicle recalls the ACCC also lists,
          which carry a REC number; every one of those is checked against the vehicle register on each build.</li>
          <li><strong>Product categories are applied using today's taxonomy</strong>,
          including to recalls from decades ago. A trend in categories is therefore partly
          a trend in how the ACCC classifies things — which is stated again on the chart
          it affects.</li>
          <li><strong>A recall count is not a danger ranking.</strong> Big companies sell
          more products and recall more of them, and a company that recalls promptly looks
          worse than one that does not. Nothing here ranks suppliers by how dangerous they
          are, because this data cannot support that.</li>
          <li><strong>${meta.accc.partialYear} is a partial year.</strong> It is never drawn as
          a completed bar and never included in a trend line.</li>
          <li><strong>Recalls before 1998 are sparse</strong> — a handful of entries in total —
          so time series start at 1998 rather than pretending the 1980s are comparable.</li>
          <li><strong>There is no map.</strong> The register records where a product was sold
          as a mixture of "Nationally", a sales channel and occasionally a state. Most
          recalls are national, so a map would mostly redraw Australia's population and call
          it a finding.</li>
        </ul>

        <h3>Attribution and licensing</h3>
        <p>Consumer product recall data © Australian Competition and Consumer Commission,
        used under <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener">CC BY 4.0</a>.
        Vehicle recall data © Commonwealth of Australia (Department of Infrastructure,
        Transport, Regional Development, Communications, Sport and the Arts). Neither
        department endorses this site. Vehicle identification number lists published by the
        vehicle register are deliberately not reproduced here.</p>
        <p>This is an independent project and is not affiliated with either regulator. For
        the official notice, always follow the link through to the register.</p>
      `;

      panel.append(head, body);
    },
  });
}
